import React, { useState, useEffect } from "react";
import api from "../services/apiClient";
import MixtureImage from "images/mixture_image.png";
import {
  usePatientOrder,
  updateLineItemQuantity,
  optimisticLineItemUpdate,
} from "../data/patientOrder";
import {
  CheckIcon,
  ClockIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import Tooltip from "../components/tooltip";

export default function OrderSummary(props) {
  const { patientOrder, isLoading, isError, mutate } = usePatientOrder(
    props.token
  );
  const [allowRestockEmails, setAllowRestockEmails] = useState(true);
  const [notificationUpdateStatus, setNotificationUpdateStatus] = useState("");

  useEffect(() => {
    if (patientOrder && patientOrder.patient) {
      setAllowRestockEmails(patientOrder.patient.allow_restock_emails === true);
    }
  }, [patientOrder]);

  const handleNotificationPreferenceChange = async (event) => {
    const newPreference = event.target.checked;
    setAllowRestockEmails(newPreference);
    setNotificationUpdateStatus("Updating...");
    try {
      await api.patch("/api/patient/settings/notifications", {
        token: props.token,
        allow_restock_emails: newPreference,
      });
      mutate();
      setNotificationUpdateStatus("Preferences updated successfully.");
      setTimeout(() => setNotificationUpdateStatus(""), 3000);
    } catch (error) {
      console.error("Failed to update notification settings", error);
      setNotificationUpdateStatus(
        "Failed to update preferences. Please try again."
      );
      setAllowRestockEmails(!newPreference);
      setTimeout(() => setNotificationUpdateStatus(""), 3000);
    }
  };

  const calcPrice = (lineItem) =>
    `$${(lineItem.patient_price * lineItem.ordering_quantity).toFixed(2)}`;

  const renderLineItem = (lineItem) => {
    return (
      <li
        key={lineItem.id}
        className="product-item flex px-4 py-6 sm:px-6"
      >
        <div className="shrink-0 max-h-20">
          <img
            src={lineItem.mixture ? MixtureImage : lineItem.image_url}
            className="w-10 max-h-full rounded-md object-contain"
          />
        </div>

        <div className="ml-6 flex flex-1 flex-col">
          <div className="flex">
            <div className="min-w-0 flex-1">
              <div className="flex flex-1 items-end justify-between pt-2">
                <h4 className="text-sm font-medium text-gray-700">
                  {lineItem.name}
                </h4>
                <p className="mt-1 text-sm font-medium text-gray-900 ml-4">
                  ${lineItem.patient_price.toFixed(2)}
                </p>
              </div>
              <div>
                {lineItem.mixture && lineItem.herbs && (
                  <p className="mt-1 text-sm text-gray-700">
                    {lineItem.herbs}
                  </p>
                )}
                {lineItem.note && (
                  <p className="mt-1 text-sm text-gray-700">
                    {lineItem.note}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-1 items-end justify-between pt-2">
            {itemStatus(lineItem)}
            {quantityForm(lineItem)}
          </div>
        </div>
      </li>
    );
  };

  const itemStatus = (lineItem) => {
    let statusDetails;

    if (lineItem.fully_ordered) {
      statusDetails = {
        icon: (
          <ClipboardDocumentCheckIcon className="h-5 w-5 shrink-0 text-gray-300" />
        ),
        text: "Fully Ordered",
      };
    } else if (!lineItem.active) {
      statusDetails = {
        icon: (
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-gray-300" />
        ),
        text: "Product Discontinued",
      };
    } else if (lineItem.temporarily_unavailable) {
      // Ticket 25660 — a herb the dispensary flagged as long-term OOS. Blocks the
      // order server-side; here we tell the patient why.
      statusDetails = {
        icon: (
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-500" />
        ),
        text: "Temporarily unavailable",
      };
    } else if (lineItem.max_q == 0) {
      statusDetails = {
        icon: (
          <ClockIcon className="h-5 w-5 shrink-0 text-gray-300" />
        ),
        text: "Out of stock",
      };
    } else {
      statusDetails = {
        icon: (
          <CheckIcon className="h-5 w-5 shrink-0 text-green-500" />
        ),
        text: "In stock",
      };
    }

    return (
      <p className="mt-4 flex space-x-2 text-sm text-gray-500">
        {statusDetails.icon}
        <span>{statusDetails.text}</span>
      </p>
    );
  };

  const handleQuanityChange = (line_item_id, quantity, is_mixture) => {
    mutate(
      updateLineItemQuantity({
        token: props.token,
        line_item_id: line_item_id,
        quantity: quantity,
        is_mixture: is_mixture,
      }),
      {
        optimisticData: (patientOrder) =>
          optimisticLineItemUpdate(
            patientOrder,
            line_item_id,
            "ordering_quantity",
            quantity
          ),
        populateCache: true,
      }
    );
  };

  const quantityForm = (lineItem) => {
    const options = Array.from({ length: lineItem.max_q + 1 }, (_, i) => (
      <option key={i} value={i}>
        {i}
      </option>
    ));

    return (
      <div className={`ml-4 mt-4 flex items-start gap-2`}>
        <Tooltip text="Select the quantity to purchase now, up to the prescribed maximum. Remaining quantity can be ordered later." />
        <select
          id="quantity"
          name="quantity"
          disabled={lineItem.max_q == 0}
          onChange={(e) =>
            handleQuanityChange(
              lineItem.id,
              parseInt(e.target.value),
              lineItem.mixture
            )
          }
          className="rounded-md border border-gray-300 text-left text-base font-medium text-gray-700 shadow-xs focus:border-indigo-500 focus:outline-hidden focus:ring-1 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:opacity-60"
          defaultValue={lineItem.ordering_quantity}
        >
          {options}
        </select>
      </div>
    );
  };

  const renderLineItems = () => {
    return (
      patientOrder.line_items &&
      patientOrder.line_items.map((lineItem, i) => {
        return renderLineItem(lineItem);
      })
    );
  };

  const Skeleton = () => {
    return (
      <div className="animate-pulse">
        <div className="rounded-lg border border-gray-200 bg-white shadow-xs">
          <ul role="list" className="divide-y divide-gray-200">
            <li className="flex px-4 py-6 sm:px-6">
              <div className="shrink-0 h-20 bg-gray-200 w-10"></div>
              <div className="ml-6">
                <div className="flex flex-col items-start">
                  <h4 className="text-sm font-medium text-gray-200 bg-gray-200">
                    Lorem ipsum
                  </h4>
                  <p className="mt-1 text-sm text-gray-200 bg-gray-200">
                    Lorem ipsum dolor sit amet, consectetur adipiscing
                  </p>
                </div>
              </div>
            </li>
          </ul>
          <dl className="space-y-6 border-t border-gray-200 px-4 py-6 sm:px-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-200 bg-gray-200">
                Subtotal
              </p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-200 bg-gray-200">
                Shipping
              </p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-200 bg-gray-200">Tax</p>
            </div>
            <div className="flex items-center justify-between border-t border-gray-200 pt-6">
              <p className="text-base font-medium text-gray-200 bg-gray-200">
                Total
              </p>
              <dd className="text-base font-medium text-gray-200 bg-gray-200">
                $123.45
              </dd>
            </div>
          </dl>
        </div>
      </div>
    );
  };

  if (isLoading) return <Skeleton />;

  return (
    <div>
      <div className="rounded-lg border border-gray-200 bg-white shadow-xs">
        <ul role="list" className="divide-y divide-gray-200">
          {!isLoading && !isError && patientOrder && renderLineItems()}
        </ul>
        <dl className="space-y-6 border-t border-gray-200 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <p className="text-sm">Subtotal</p>
            <dd
              id="subtotal"
              className="text-sm font-medium text-gray-900"
            >
              ${parseFloat(patientOrder?.sub_total).toFixed(2)}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm">Shipping</p>
            <dd
              id="shipping"
              className="text-sm font-medium text-gray-900"
            >
              ${parseFloat(patientOrder?.shipping.rate).toFixed(2)}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm">Tax</p>
            <dd id="tax" className="text-sm font-medium text-gray-900">
              ${parseFloat(patientOrder?.gst).toFixed(2)}
            </dd>
          </div>
          <div className="flex items-center justify-between border-t border-gray-200 pt-6">
            <p className="text-base font-medium">Total</p>
            <dd
              id="total"
              className="text-base font-medium text-gray-900"
            >
              ${parseFloat(patientOrder?.total).toFixed(2)}
            </dd>
          </div>
        </dl>
      </div>

      {!isLoading && !isError && patientOrder && patientOrder.patient && (
        <div className="mt-8 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <label
                htmlFor="allowRestockEmailsCheckbox"
                className="flex items-center cursor-pointer"
              >
                <input
                  type="checkbox"
                  id="allowRestockEmailsCheckbox"
                  className="h-4 w-4 text-integria-green border-gray-300 rounded-sm focus:ring-integria-green"
                  checked={allowRestockEmails}
                  onChange={handleNotificationPreferenceChange}
                />
                <span className="ml-2 text-sm text-gray-700">
                  Email me on restock
                </span>
                {notificationUpdateStatus && (
                  <p
                    className={`ml-2 text-xs ${notificationUpdateStatus.startsWith("Failed") ? "text-red-600" : "text-green-600"}`}
                  >
                    {notificationUpdateStatus}
                  </p>
                )}
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
